import React, { useCallback } from 'react';

import { FormattedMessage } from 'react-intl-next';

import { withAnalyticsEvents, type WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import Checkbox from '@atlaskit/checkbox';
import { Field } from '@atlaskit/form';
import { fg } from '@atlaskit/platform-feature-flags';
import { componentWithFG } from '@atlaskit/platform-feature-flags-react';
import { Text } from '@atlaskit/primitives/compiled';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics';

import { TeamMembershipSettings } from '../../types';
import { triggerAnalyticsForTeamTypePickerInteraction } from '../analytics';
import { messages } from '../i18n/messages';

import { CheckboxLine } from './styled';

interface TeamTypePickerProps {
	defaultValue?: TeamMembershipSettings;
	onChange: (newValue: TeamMembershipSettings) => void;
}

export const TeamTypePicker = ({
	createAnalyticsEvent,
	defaultValue,
	onChange,
}: TeamTypePickerProps & WithAnalyticsEventsProps) => {
	const { fireEvent } = useAnalyticsEvents();

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const chosenValue = event.currentTarget.checked
				? TeamMembershipSettings.OPEN
				: TeamMembershipSettings.INVITE_ONLY;

			if (fg('ptc-enable-teams-common-analytics-refactor')) {
				fireEvent('ui.teamTypePicker.clicked', {
					defaultType: defaultValue || null,
					chosenType: chosenValue,
				});
			} else {
				triggerAnalyticsForTeamTypePickerInteraction(
					createAnalyticsEvent,
					defaultValue,
					chosenValue,
				);
			}

			onChange(chosenValue);
		},
		[createAnalyticsEvent, defaultValue, onChange, fireEvent],
	);

	const label = (
		<Text size="small">
			<FormattedMessage {...messages.teamTypeCheckboxLabel} />
		</Text>
	);

	return (
		<Field name="teamType" label={<FormattedMessage {...messages.teamTypePickerLabel} />}>
			{() => (
				<CheckboxLine>
					<Checkbox
						{...(fg('team_type_picker_a11y_fix') ? { name: 'teamType' } : {})}
						defaultChecked={defaultValue === TeamMembershipSettings.OPEN}
						onChange={handleChange}
						label={label}
					/>
				</CheckboxLine>
			)}
		</Field>
	);
};

export default componentWithFG(
	'ptc-enable-teams-common-analytics-refactor',
	TeamTypePicker,
	withAnalyticsEvents()(TeamTypePicker),
);
